import React, { useEffect } from 'react';
import classNames from 'classnames';
import { Box, Flex, FlexColumn, Icon, Tooltip } from 'core/components';
import WidgetFrame from 'app/components/decks/widgets/WidgetFrame';
import { getBoardPanelComponent } from 'app/stores/boards/boardPanelTypes';
import $boards from 'app/stores/boards/$boards';
import BoardCard from 'app/views/boards/BoardCard';
import { observer } from 'mobx-react';
import { getWidgetSize, getWidgetTitle } from 'app/views/boards/panels/widget/utils';
import PanelTitle from 'app/views/boards/PanelTitle';
import { useHistory } from 'react-router-dom';

const BoardPanelView = React.forwardRef((props, ref) => {
  const {
    children,
    className,
    dragHeaderCls,
    panel,
    board,
    form,
    height,
    approxWidth,
    onAddPanel,
    onRemovePanel,
    interactive,
    customRightElements,
    canCustomize = true,
    onSaveTitle,
    onSectionChange,
    onSectionLayoutChange,
    onEditPanel,
    onDuplicatePanel,
    ...rest
  } = props;
  const { location } = useHistory();

  const {
    component: BoardPanelComponent,
    options: BoardPanelOptions,
    titleTools: BoardPanelTitleTools,
    autoSize
  } = getBoardPanelComponent(panel);

  useEffect(() => {
    // scroll to the panel we just added or edited
    if (ref?.current instanceof HTMLElement && location.state?.panelId === panel.id) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  let { title } = panel;
  const tooltip = panel.get('config.description');
  const isPreview = !!form;
  let renderSize = {};

  let config = structuredClone(panel.get('config')) || {};
  let showTitle = config.showTitle ?? true;

  if (isPreview) {
    const formValues = form.getValues();
    showTitle = false;
    title = formValues.title;

    // merge form config over panel config
    if (panel.get('type') === 'ude') {
      // pull non-query config from the form in edit mode
      config.data = { ...config.data, measurement: formValues.config.data?.measurement };
      config.chart = formValues.config.chart;
      config.table = formValues.config.table;
    } else {
      config = { ...config, ...formValues.config };
    }

    if (panel.get('type') === 'widget') {
      const { widgetName } = formValues.config;
      // in edit mode, render the widget in a sensible size
      renderSize = getWidgetSize(widgetName);
      title ||= getWidgetTitle(formValues.config);
    } else {
      title ||= 'Untitled';
    }
  }

  // UDE panels in empty state (no metrics yet) should hide the title bar and card chrome
  const isUdeEmpty = isPreview && panel.get('type') === 'ude' && !config.data?.metrics?.length;

  const handleSaveTitle = (newTitle) => {
    if (form) {
      form.setValue('title', newTitle);
    }
    if (onSaveTitle) {
      onSaveTitle(newTitle);
    } else {
      panel.save({ title: newTitle }, { optimistic: true, draft: true });
    }
  };

  const titleCmp = (
    <Flex gap="4px" flex={1} alignItems="center">
      {BoardPanelTitleTools && canCustomize && (
        <BoardPanelTitleTools panel={panel} onSectionLayoutChange={onSectionLayoutChange} />
      )}
      <PanelTitle panel={panel} title={title} onSave={handleSaveTitle} />
      {canCustomize && panel.isDirty && (
        <Tooltip content="Unsaved Changes">
          <Icon icon="dot" color="primary" />
        </Tooltip>
      )}
    </Flex>
  );

  const WrapperComponent = isUdeEmpty ? FlexColumn : BoardCard;

  const isTextPanel = panel.get('type') === 'text';
  const isTransparentTextPanel = isTextPanel && (!config.backgroundColor || config.backgroundColor === 'transparent');

  const handleTextPanelClick = (e) => {
    if (isTextPanel && canCustomize && !isPreview && !e.target.closest('a')) {
      onEditPanel(panel);
    }
  };

  return (
    <WrapperComponent
      ref={ref}
      {...rest}
      className={classNames(
        {
          interactive,
          'react-resizable-hide': autoSize,
          highlighted: $boards.highlightedPanelIds.has(panel.id),
          'text-transparent-bg': isTransparentTextPanel
        },
        className,
        `type-${panel.get('type')}`
      )}
      showTitle={isTextPanel ? false : showTitle}
      flex={1}
      {...renderSize}
    >
      {panel.get('type') === 'widget' && BoardPanelComponent ? (
        <BoardPanelComponent
          board={board}
          panel={panel}
          form={form}
          config={config}
          height={height}
          approxWidth={approxWidth}
          canCustomize={canCustomize}
          menuActions={
            BoardPanelOptions && (
              <BoardPanelOptions
                board={board}
                panel={panel}
                onSectionChange={onSectionChange}
                onSectionLayoutChange={onSectionLayoutChange}
              />
            )
          }
          onRemove={() => onRemovePanel(panel)}
          onDuplicate={onDuplicatePanel ? () => onDuplicatePanel(panel) : undefined}
          configAction={() => onEditPanel(panel)}
          title={titleCmp}
          dragHeaderCls={dragHeaderCls}
          showTitle={showTitle}
          showTopBar={!isPreview}
          tooltip={tooltip}
          titleOverride
        />
      ) : (
        <WidgetFrame
          canCustomize={canCustomize}
          customRightElements={isUdeEmpty ? null : customRightElements}
          menuActions={
            BoardPanelOptions && (
              <BoardPanelOptions
                board={board}
                panel={panel}
                onSectionChange={onSectionChange}
                onSectionLayoutChange={onSectionLayoutChange}
              />
            )
          }
          onRemove={() => onRemovePanel(panel)}
          onDuplicate={onDuplicatePanel ? () => onDuplicatePanel(panel) : undefined}
          configAction={panel.get('type') !== 'section' ? () => onEditPanel(panel) : undefined}
          title={titleCmp}
          dragHeaderCls={dragHeaderCls}
          showTitle={isTextPanel ? false : showTitle}
          showTopBar={!isPreview}
          display="flex"
          tooltip={tooltip}
          titleOverride
        >
          {BoardPanelComponent && isTextPanel && (
            <div
              onClick={handleTextPanelClick}
              style={{
                cursor: canCustomize ? 'pointer' : 'default',
                flex: 1,
                display: 'flex'
              }}
              role={canCustomize ? 'button' : undefined}
              tabIndex={canCustomize ? 0 : undefined}
            >
              <BoardPanelComponent
                board={board}
                panel={panel}
                form={form}
                config={config}
                canCustomize={canCustomize}
              />
            </div>
          )}
          {BoardPanelComponent && !isTextPanel && (
            <BoardPanelComponent
              board={board}
              panel={panel}
              form={form}
              config={config}
              onSectionChange={onSectionChange}
              onSectionLayoutChange={onSectionLayoutChange}
              onEditPanel={onEditPanel}
              onDuplicatePanel={onDuplicatePanel}
              canCustomize={canCustomize}
            />
          )}
          {!BoardPanelComponent && <Box p={2}>Panel Type Not Found</Box>}
        </WidgetFrame>
      )}
      {children}
    </WrapperComponent>
  );
});

export default observer(BoardPanelView);
